<template>
  <div class="form-section">
    <h2 class="section-title">2. 예산 관리</h2>
    <p class="section-description">이벤트 예산을 회차별로 관리하고, 쿠폰 외 지출 내역을 추적합니다.</p>

    <!-- 탭 UI -->
    <a-tabs v-model:activeKey="activeTab" class="budget-tabs">
      <!-- 예산 회차 탭 -->
      <a-tab-pane key="rounds" tab="예산 회차">
        <div class="tab-content">
          <div class="section-header">
            <h3>예산 회차 목록 ({{ form.budgetRounds?.length || 0 }}건)</h3>
            <a-button type="primary" class="yellow-button" @click="handleAddRound">
              <i class="fa-solid fa-plus"></i> 회차 추가
            </a-button>
          </div>

          <a-table
            :dataSource="form.budgetRounds"
            :columns="roundColumns"
            :pagination="false"
            :rowKey="(record, index) => index"
            :locale="{ emptyText: '등록된 예산 회차가 없습니다. 회차를 추가해주세요.' }"
          >
            <template #bodyCell="{ column, record, index }">
              <template v-if="column.key === 'roundNumber'">
                <span class="round-badge">{{ record.roundNumber }}회차</span>
              </template>
              <template v-else-if="column.key === 'amount'">
                <span class="amount">{{ formatCurrency(record.amount) }}</span>
              </template>
              <template v-else-if="column.key === 'cumulativeTotal'">
                <span class="cumulative">{{ formatCurrency(getCumulativeTotal(index)) }}</span>
              </template>
              <template v-else-if="column.key === 'period'">
                <span v-if="record.startDate || record.endDate">
                  {{ formatDate(record.startDate) }} ~ {{ formatDate(record.endDate) }}
                </span>
                <span v-else class="no-data">-</span>
              </template>
              <template v-else-if="column.key === 'status'">
                <a-tag :color="getStatusColor(record.status)">
                  {{ getStatusText(record.status) }}
                </a-tag>
              </template>
              <template v-else-if="column.key === 'description'">
                <span v-if="record.description">{{ record.description }}</span>
                <span v-else class="no-data">-</span>
              </template>
              <template v-else-if="column.key === 'action'">
                <a-space>
                  <a-button type="link" size="small" @click="handleEditRound(index)">
                    <i class="fa-solid fa-edit"></i>
                  </a-button>
                  <a-popconfirm
                    title="이 회차를 삭제하시겠습니까?"
                    ok-text="삭제"
                    cancel-text="취소"
                    @confirm="handleDeleteRound(index)"
                  >
                    <a-button type="link" size="small" danger>
                      <i class="fa-solid fa-trash"></i>
                    </a-button>
                  </a-popconfirm>
                </a-space>
              </template>
            </template>
          </a-table>
        </div>
      </a-tab-pane>

      <!-- 지출 내역 탭 -->
      <a-tab-pane key="expenses" tab="지출 내역">
        <div class="tab-content">
          <div class="section-header">
            <h3>지출 내역 목록 ({{ form.expenses?.length || 0 }}건)</h3>
            <a-button type="primary" class="yellow-button" @click="handleAddExpense">
              <i class="fa-solid fa-plus"></i> 지출 추가
            </a-button>
          </div>

          <a-table
            :dataSource="form.expenses"
            :columns="expenseColumns"
            :pagination="false"
            :rowKey="(record, index) => index"
            :locale="{ emptyText: '등록된 지출 내역이 없습니다. 지출을 추가해주세요.' }"
          >
            <template #bodyCell="{ column, record, index }">
              <template v-if="column.key === 'category'">
                <a-tag :color="getCategoryColor(record.category)">
                  {{ getCategoryText(record.category, record.customCategoryName) }}
                </a-tag>
              </template>
              <template v-else-if="column.key === 'amount'">
                <span class="expense-amount">{{ formatCurrency(record.amount) }}</span>
              </template>
              <template v-else-if="column.key === 'expenseDate'">
                <span>{{ formatDate(record.expenseDate) }}</span>
              </template>
              <template v-else-if="column.key === 'description'">
                <span v-if="record.description">{{ record.description }}</span>
                <span v-else class="no-data">-</span>
              </template>
              <template v-else-if="column.key === 'action'">
                <a-space>
                  <a-button type="link" size="small" @click="handleEditExpense(index)">
                    <i class="fa-solid fa-edit"></i>
                  </a-button>
                  <a-popconfirm
                    title="이 지출 내역을 삭제하시겠습니까?"
                    ok-text="삭제"
                    cancel-text="취소"
                    @confirm="handleDeleteExpense(index)"
                  >
                    <a-button type="link" size="small" danger>
                      <i class="fa-solid fa-trash"></i>
                    </a-button>
                  </a-popconfirm>
                </a-space>
              </template>
            </template>
          </a-table>
        </div>
      </a-tab-pane>
    </a-tabs>

    <!-- 예산 메모 -->
    <div class="form-row" style="margin-top: 24px;">
      <form-item :fullWidth="true">
        <a-form-item label="예산 메모" name="budgetNote">
          <a-textarea
            v-model:value="form.budgetNote"
            placeholder="예산 관련 메모를 입력하세요 (예: 총 5회차 예정, 분기별 증액 검토)"
            :rows="3"
          />
        </a-form-item>
      </form-item>
    </div>

    <!-- 회차 추가/수정 모달 -->
    <a-modal
      v-model:visible="roundModalVisible"
      :title="isRoundEditMode ? '예산 회차 수정' : '예산 회차 추가'"
      :confirm-loading="modalLoading"
      :footer="null"
      @cancel="handleRoundModalCancel"
      width="500px"
    >
      <template #title>
        <div class="modal-title">{{ isRoundEditMode ? '예산 회차 수정' : '예산 회차 추가' }}</div>
      </template>

      <div class="modal-content">
        <a-form :model="roundForm" layout="vertical">
          <a-form-item label="회차 번호" required>
            <a-input-number
              v-model:value="roundForm.roundNumber"
              :min="1"
              style="width: 100%"
              placeholder="회차 번호"
            />
          </a-form-item>

          <a-form-item label="예산 금액 (원)" required>
            <a-input-number
              v-model:value="roundForm.amount"
              :min="0"
              :step="100000"
              :formatter="value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
              :parser="value => value.replace(/\$\s?|(,*)/g, '')"
              style="width: 100%"
              placeholder="예: 2000000"
            />
          </a-form-item>

          <a-form-item label="회차 설명">
            <a-input
              v-model:value="roundForm.description"
              placeholder="예: 1차 예산 배정"
            />
          </a-form-item>

          <a-form-item label="기간">
            <a-range-picker
              v-model:value="roundForm.dateRange"
              style="width: 100%"
              format="YYYY-MM-DD"
            />
          </a-form-item>

          <a-form-item label="상태">
            <a-select v-model:value="roundForm.status" style="width: 100%">
              <a-select-option value="planned">예정</a-select-option>
              <a-select-option value="active">진행중</a-select-option>
              <a-select-option value="completed">완료</a-select-option>
            </a-select>
          </a-form-item>
        </a-form>

        <div class="modal-footer">
          <a-button @click="handleRoundModalCancel">취소</a-button>
          <a-button type="primary" :loading="modalLoading" @click="handleRoundModalSubmit" class="yellow-button">
            {{ isRoundEditMode ? '수정' : '추가' }}
          </a-button>
        </div>
      </div>
    </a-modal>

    <!-- 지출 추가/수정 모달 -->
    <a-modal
      v-model:visible="expenseModalVisible"
      :title="isExpenseEditMode ? '지출 내역 수정' : '지출 내역 추가'"
      :confirm-loading="modalLoading"
      :footer="null"
      @cancel="handleExpenseModalCancel"
      width="500px"
    >
      <template #title>
        <div class="modal-title">{{ isExpenseEditMode ? '지출 내역 수정' : '지출 내역 추가' }}</div>
      </template>

      <div class="modal-content">
        <a-form :model="expenseForm" layout="vertical">
          <a-form-item label="지출 카테고리" required>
            <a-select v-model:value="expenseForm.category" style="width: 100%" placeholder="카테고리 선택">
              <a-select-option value="marketing">광고/마케팅비</a-select-option>
              <a-select-option value="operation">운영/인건비</a-select-option>
              <a-select-option value="misc">기타 경비</a-select-option>
              <a-select-option value="custom">직접 입력</a-select-option>
            </a-select>
          </a-form-item>

          <a-form-item v-if="expenseForm.category === 'custom'" label="카테고리명" required>
            <a-input
              v-model:value="expenseForm.customCategoryName"
              placeholder="예: 행사 대행비"
            />
          </a-form-item>

          <a-form-item label="지출 금액 (원)" required>
            <a-input-number
              v-model:value="expenseForm.amount"
              :min="0"
              :step="10000"
              :formatter="value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
              :parser="value => value.replace(/\$\s?|(,*)/g, '')"
              style="width: 100%"
              placeholder="예: 500000"
            />
          </a-form-item>

          <a-form-item label="지출 일자" required>
            <a-date-picker
              v-model:value="expenseForm.expenseDate"
              style="width: 100%"
              format="YYYY-MM-DD"
              placeholder="지출 일자 선택"
            />
          </a-form-item>

          <a-form-item label="지출 설명">
            <a-textarea
              v-model:value="expenseForm.description"
              placeholder="예: 페이스북 광고 집행"
              :rows="2"
            />
          </a-form-item>
        </a-form>

        <div class="modal-footer">
          <a-button @click="handleExpenseModalCancel">취소</a-button>
          <a-button type="primary" :loading="modalLoading" @click="handleExpenseModalSubmit" class="yellow-button">
            {{ isExpenseEditMode ? '수정' : '추가' }}
          </a-button>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { defineComponent, ref, computed, reactive, getCurrentInstance } from 'vue';
import dayjs from 'dayjs';
import FormItem from '../ui/FormItem.vue';

export default defineComponent({
  name: 'BudgetSection',
  components: {
    FormItem
  },
  props: {
    form: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const { proxy: app } = getCurrentInstance();

    // 탭 상태
    const activeTab = ref('rounds');

    // 회차 모달 상태
    const roundModalVisible = ref(false);
    const modalLoading = ref(false);
    const isRoundEditMode = ref(false);
    const editingRoundIndex = ref(-1);

    // 지출 모달 상태
    const expenseModalVisible = ref(false);
    const isExpenseEditMode = ref(false);
    const editingExpenseIndex = ref(-1);

    // 회차 폼 데이터
    const roundForm = reactive({
      roundNumber: 1,
      amount: 0,
      description: '',
      dateRange: null,
      status: 'planned'
    });

    // 지출 폼 데이터
    const expenseForm = reactive({
      category: 'marketing',
      customCategoryName: '',
      amount: 0,
      expenseDate: null,
      description: ''
    });

    // 회차 테이블 컬럼 정의
    const roundColumns = [
      { title: '회차', key: 'roundNumber', dataIndex: 'roundNumber', width: 80 },
      { title: '예산 금액', key: 'amount', dataIndex: 'amount', width: 150 },
      { title: '누적 총액', key: 'cumulativeTotal', width: 150 },
      { title: '기간', key: 'period', width: 200 },
      { title: '상태', key: 'status', dataIndex: 'status', width: 100 },
      { title: '설명', key: 'description', dataIndex: 'description' },
      { title: '관리', key: 'action', width: 100, fixed: 'right' }
    ];

    // 지출 테이블 컬럼 정의
    const expenseColumns = [
      { title: '카테고리', key: 'category', dataIndex: 'category', width: 150 },
      { title: '금액', key: 'amount', dataIndex: 'amount', width: 150 },
      { title: '지출일', key: 'expenseDate', dataIndex: 'expenseDate', width: 120 },
      { title: '설명', key: 'description', dataIndex: 'description' },
      { title: '관리', key: 'action', width: 100, fixed: 'right' }
    ];

    // 총 예산 계산
    const calculatedTotalBudget = computed(() => {
      if (!props.form.budgetRounds || props.form.budgetRounds.length === 0) return 0;
      return props.form.budgetRounds.reduce((sum, round) => sum + (round.amount || 0), 0);
    });

    // 총 지출 계산
    const calculatedTotalExpense = computed(() => {
      if (!props.form.expenses || props.form.expenses.length === 0) return 0;
      return props.form.expenses.reduce((sum, expense) => sum + (expense.amount || 0), 0);
    });

    // 잔여 예산 계산
    const remainingBudget = computed(() => {
      return calculatedTotalBudget.value - calculatedTotalExpense.value;
    });

    // 현재 진행 회차
    const currentActiveRound = computed(() => {
      if (!props.form.budgetRounds || props.form.budgetRounds.length === 0) return '-';
      const activeRound = props.form.budgetRounds.find(r => r.status === 'active');
      return activeRound ? `${activeRound.roundNumber}회차` : '-';
    });

    // 누적 총액 계산
    const getCumulativeTotal = (index) => {
      if (!props.form.budgetRounds) return 0;
      let total = 0;
      for (let i = 0; i <= index; i++) {
        total += props.form.budgetRounds[i]?.amount || 0;
      }
      return total;
    };

    // 금액 포맷
    const formatCurrency = (value) => {
      if (!value && value !== 0) return '-';
      return new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(value);
    };

    // 날짜 포맷
    const formatDate = (date) => {
      if (!date) return '';
      return dayjs(date).format('YYYY-MM-DD');
    };

    // 회차 상태 색상
    const getStatusColor = (status) => {
      const colors = {
        planned: 'blue',
        active: 'green',
        completed: 'gray'
      };
      return colors[status] || 'default';
    };

    // 회차 상태 텍스트
    const getStatusText = (status) => {
      const texts = {
        planned: '예정',
        active: '진행중',
        completed: '완료'
      };
      return texts[status] || status;
    };

    // 지출 카테고리 색상
    const getCategoryColor = (category) => {
      const colors = {
        marketing: 'orange',
        operation: 'purple',
        misc: 'cyan',
        custom: 'geekblue'
      };
      return colors[category] || 'default';
    };

    // 지출 카테고리 텍스트
    const getCategoryText = (category, customName) => {
      if (category === 'custom' && customName) {
        return customName;
      }
      const texts = {
        marketing: '광고/마케팅비',
        operation: '운영/인건비',
        misc: '기타 경비',
        custom: '직접 입력'
      };
      return texts[category] || category;
    };

    // ========== 회차 관련 핸들러 ==========

    // 회차 추가
    const handleAddRound = () => {
      isRoundEditMode.value = false;
      editingRoundIndex.value = -1;

      const nextRoundNumber = (props.form.budgetRounds?.length || 0) + 1;

      Object.assign(roundForm, {
        roundNumber: nextRoundNumber,
        amount: 0,
        description: '',
        dateRange: null,
        status: 'planned'
      });

      roundModalVisible.value = true;
    };

    // 회차 수정
    const handleEditRound = (index) => {
      isRoundEditMode.value = true;
      editingRoundIndex.value = index;

      const round = props.form.budgetRounds[index];
      Object.assign(roundForm, {
        roundNumber: round.roundNumber,
        amount: round.amount,
        description: round.description || '',
        dateRange: round.startDate && round.endDate ? [dayjs(round.startDate), dayjs(round.endDate)] : null,
        status: round.status || 'planned'
      });

      roundModalVisible.value = true;
    };

    // 회차 삭제
    const handleDeleteRound = (index) => {
      props.form.budgetRounds.splice(index, 1);
      app.$message.success('회차가 삭제되었습니다.');
    };

    // 회차 모달 취소
    const handleRoundModalCancel = () => {
      roundModalVisible.value = false;
    };

    // 회차 모달 제출
    const handleRoundModalSubmit = () => {
      if (!roundForm.roundNumber || roundForm.roundNumber < 1) {
        app.$message.warning('회차 번호를 입력해주세요.');
        return;
      }
      if (!roundForm.amount || roundForm.amount < 0) {
        app.$message.warning('예산 금액을 입력해주세요.');
        return;
      }

      modalLoading.value = true;

      const roundData = {
        roundNumber: roundForm.roundNumber,
        amount: roundForm.amount,
        description: roundForm.description,
        startDate: roundForm.dateRange?.[0] || null,
        endDate: roundForm.dateRange?.[1] || null,
        status: roundForm.status,
        createdAt: new Date().toISOString()
      };

      if (isRoundEditMode.value) {
        props.form.budgetRounds[editingRoundIndex.value] = roundData;
        app.$message.success('회차가 수정되었습니다.');
      } else {
        if (!props.form.budgetRounds) {
          props.form.budgetRounds = [];
        }
        props.form.budgetRounds.push(roundData);
        app.$message.success('회차가 추가되었습니다.');
      }

      props.form.budgetRounds.sort((a, b) => a.roundNumber - b.roundNumber);

      modalLoading.value = false;
      roundModalVisible.value = false;
    };

    // ========== 지출 관련 핸들러 ==========

    // 지출 추가
    const handleAddExpense = () => {
      isExpenseEditMode.value = false;
      editingExpenseIndex.value = -1;

      Object.assign(expenseForm, {
        category: 'marketing',
        customCategoryName: '',
        amount: 0,
        expenseDate: dayjs(),
        description: ''
      });

      expenseModalVisible.value = true;
    };

    // 지출 수정
    const handleEditExpense = (index) => {
      isExpenseEditMode.value = true;
      editingExpenseIndex.value = index;

      const expense = props.form.expenses[index];
      Object.assign(expenseForm, {
        category: expense.category,
        customCategoryName: expense.customCategoryName || '',
        amount: expense.amount,
        expenseDate: expense.expenseDate ? dayjs(expense.expenseDate) : null,
        description: expense.description || ''
      });

      expenseModalVisible.value = true;
    };

    // 지출 삭제
    const handleDeleteExpense = (index) => {
      props.form.expenses.splice(index, 1);
      app.$message.success('지출 내역이 삭제되었습니다.');
    };

    // 지출 모달 취소
    const handleExpenseModalCancel = () => {
      expenseModalVisible.value = false;
    };

    // 지출 모달 제출
    const handleExpenseModalSubmit = () => {
      if (!expenseForm.category) {
        app.$message.warning('지출 카테고리를 선택해주세요.');
        return;
      }
      if (expenseForm.category === 'custom' && !expenseForm.customCategoryName) {
        app.$message.warning('카테고리명을 입력해주세요.');
        return;
      }
      if (!expenseForm.amount || expenseForm.amount <= 0) {
        app.$message.warning('지출 금액을 입력해주세요.');
        return;
      }
      if (!expenseForm.expenseDate) {
        app.$message.warning('지출 일자를 선택해주세요.');
        return;
      }

      modalLoading.value = true;

      const expenseData = {
        category: expenseForm.category,
        customCategoryName: expenseForm.category === 'custom' ? expenseForm.customCategoryName : null,
        amount: expenseForm.amount,
        expenseDate: expenseForm.expenseDate ? expenseForm.expenseDate.toISOString() : null,
        description: expenseForm.description,
        createdAt: new Date().toISOString()
      };

      if (isExpenseEditMode.value) {
        props.form.expenses[editingExpenseIndex.value] = expenseData;
        app.$message.success('지출 내역이 수정되었습니다.');
      } else {
        if (!props.form.expenses) {
          props.form.expenses = [];
        }
        props.form.expenses.push(expenseData);
        app.$message.success('지출 내역이 추가되었습니다.');
      }

      // 날짜순으로 정렬 (최신순)
      props.form.expenses.sort((a, b) => new Date(b.expenseDate) - new Date(a.expenseDate));

      modalLoading.value = false;
      expenseModalVisible.value = false;
    };

    return {
      activeTab,
      roundColumns,
      expenseColumns,
      roundModalVisible,
      expenseModalVisible,
      modalLoading,
      isRoundEditMode,
      isExpenseEditMode,
      roundForm,
      expenseForm,
      calculatedTotalBudget,
      calculatedTotalExpense,
      remainingBudget,
      currentActiveRound,
      getCumulativeTotal,
      formatCurrency,
      formatDate,
      getStatusColor,
      getStatusText,
      getCategoryColor,
      getCategoryText,
      handleAddRound,
      handleEditRound,
      handleDeleteRound,
      handleRoundModalCancel,
      handleRoundModalSubmit,
      handleAddExpense,
      handleEditExpense,
      handleDeleteExpense,
      handleExpenseModalCancel,
      handleExpenseModalSubmit
    };
  }
});
</script>

<style lang="scss" scoped>
.form-section {
  padding: 24px;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 24px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #1a1a1a;
}

.section-description {
  color: #666;
  margin-bottom: 24px;
  font-size: 14px;
}

.budget-tabs {
  :deep(.ant-tabs-nav) {
    margin-bottom: 16px;
  }
}

.tab-content {
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    h3 {
      font-size: 16px;
      font-weight: 600;
      margin: 0;
    }
  }
}

.round-badge {
  display: inline-block;
  padding: 4px 12px;
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 4px;
  color: #1890ff;
  font-weight: 500;
}

.amount {
  font-weight: 600;
  color: #1a1a1a;
}

.expense-amount {
  font-weight: 600;
  color: #ff4d4f;
}

.cumulative {
  font-weight: 600;
  color: #52c41a;
}

.no-data {
  color: #999;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
}

.modal-content {
  padding: 20px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.yellow-button {
  background-color: #fadb14 !important;
  border-color: #fadb14 !important;
  color: #1a1a1a !important;

  &:hover {
    background-color: #ffc53d !important;
    border-color: #ffc53d !important;
  }
}

.form-row {
  margin-bottom: 16px;
}
</style>
